import { showWarningNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { createBrightnessEffect } from '../stream-effects/brightness';
import { toggleBackgroundEffect } from '../virtual-background/actions';
import { SET_BRIGHTNESS_ENABLED } from './actionTypes';
import logger from './logger';
/**
 * Signals that the brightness adjustment effect has been enabled or disabled.
 *
 * @param {boolean} enabled - Whether the brightness effect is enabled.
 * @returns {{
 *     type: SET_BRIGHTNESS_ENABLED,
 *     enabled: boolean
 * }}
 */
export function setBrightnessEnabled(enabled) {
    return {
        type: SET_BRIGHTNESS_ENABLED,
        enabled
    };
}
/**
 * Enables or disables the brightness adjustment effect on the given video track.
 *
 * When a virtual background/blur is active, the brightness is composited together with it
 * by re-applying the background effect (which reads the brightness state). Otherwise a
 * standalone brightness effect is used.
 *
 * @param {boolean} enabled - Whether to enable the effect.
 * @param {Object} jitsiTrack - The local video track the effect is applied to.
 * @returns {Promise}
 */
export function toggleBrightnessEffect(enabled, jitsiTrack) {
    return async function (dispatch, getState) {
        dispatch(setBrightnessEnabled(enabled));
        const background = getState()['features/virtual-background'];
        if (background.backgroundEffectEnabled) {
            // A background/blur is active — re-apply it so the new brightness is
            // composited together with the background (Google Meet style).
            dispatch(toggleBackgroundEffect(background, jitsiTrack));
            return;
        }
        if (!jitsiTrack) {
            return;
        }
        const track = jitsiTrack;
        try {
            await track.setEffect(enabled ? createBrightnessEffect() : undefined);
        }
        catch (error) {
            logger.error('Failed to toggle brightness effect:', error);
            try {
                await track.setEffect(undefined);
            }
            catch (cleanupErr) {
                logger.warn('Failed to clear brightness effect after failure:', cleanupErr);
            }
            dispatch(setBrightnessEnabled(false));
            dispatch(showWarningNotification({ titleKey: 'videothumbnail.adjustBrightnessError' }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    };
}
